import React, { createContext, useEffect, useMemo, useState } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import useLocation from 'hooks/useLocation'

import Popup from 'components/molecules/popup'

import type { Popup as PopupType } from 'types/popup'

type PopupsProviderProps = {
  children: React.ReactNode
}

type PopupsContextType = {
  popups: PopupType[]
}

const CONTEXT_DEFAULT_VALUE: PopupsContextType = {
  popups: [],
}

const PopupsContext = createContext<PopupsContextType>(CONTEXT_DEFAULT_VALUE)

const parsePopupSlug = (slug: string) => {
  if (slug === 'strona-glowna') return '/'

  return slug
}

const parsePathname = (pathname: string) => {
  if (pathname === '/') return '/'

  return pathname.split('/').filter(Boolean).at(-1) || ''
}

export const PopupsProvider: React.FC<PopupsProviderProps> = ({ children }) => {
  const [popupToDisplay, setPopupToDisplay] = useState<PopupType | null>(null)

  const data = useStaticQuery<Queries.PopupsQuery>(graphql`
    query Popups {
      wp {
        themeSettings {
          ThemePopups {
            themePopups {
              themePopupsLink
              themePopupsImage {
                altText
                gatsbyImage(width: 1200)
              }
              themePopupsPages {
                ... on WpPage {
                  slug
                }
                ... on WpPost {
                  slug
                }
                ... on WpProduct {
                  slug
                }
                ... on WpPromotion {
                  slug
                }
                ... on WpRecipe {
                  slug
                }
                ... on WpInterview {
                  slug
                }
              }
            }
          }
        }
      }
    }
  `)

  const location = useLocation()

  const POPUPS: PopupType[] =
    data.wp?.themeSettings?.ThemePopups?.themePopups?.map((popup) => ({
      link: popup?.themePopupsLink,
      image: {
        alt: popup?.themePopupsImage?.altText || '',
        src: popup?.themePopupsImage?.gatsbyImage!,
      },
      pages:
        popup?.themePopupsPages?.flatMap((page) =>
          page?.slug ? [{ slug: parsePopupSlug(page.slug) }] : []
        ) || [],
    })) || []

  useEffect(() => {
    setPopupToDisplay(
      POPUPS.find((popup) =>
        popup.pages.find((page) =>
          page.slug.endsWith(parsePathname(location.pathname))
        )
      ) || null
    )
  }, [location.pathname])

  const contextValue = useMemo(() => ({ popups: POPUPS }), [POPUPS])

  return (
    <PopupsContext.Provider value={contextValue}>
      {children}
      {popupToDisplay && (
        <Popup
          key={popupToDisplay.pages[0].slug}
          image={popupToDisplay.image}
          link={popupToDisplay.link}
        />
      )}
    </PopupsContext.Provider>
  )
}
